/**
 * Job Description Upload Component - Upload job description for Focus Interview
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useState, useCallback, useEffect } from 'react';

// For job description RAG operations, always use the deployed API (Pinecone is configured there)
// This ensures RAG indexing works even during local development
const API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface JobDescriptionUploadProps {
  user: { username?: string; email?: string };
  getToken: () => string | null;
  onClose: () => void;
  onSuccess?: (
    jobDescription: string,
    companyName?: string,
    roleTitle?: string
  ) => void;
}

interface JobDescriptionStatus {
  has_job_description: boolean;
  vector_count?: number;
  index_name?: string;
  message?: string;
}

const JobDescriptionUpload: React.FC<JobDescriptionUploadProps> = ({
  user,
  getToken,
  onClose,
  onSuccess,
}) => {
  const [jobDescription, setJobDescription] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [roleTitle, setRoleTitle] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [jdStatus, setJdStatus] = useState<JobDescriptionStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState<boolean>(true);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';

  const checkJDStatus = useCallback(async () => {
    try {
      setCheckingStatus(true);
      const response = await fetch(
        `${API_URL}/job-description/status?username=${encodeURIComponent(
          username
        )}`
      );
      if (response && response.ok) {
        const data = await response.json();
        setJdStatus(data);
      } else {
        setJdStatus({
          has_job_description: false,
          message: 'Failed to check status',
        });
      }
    } catch (err) {
      console.error('Error checking job description status:', err);
      setJdStatus({
        has_job_description: false,
        message: 'Failed to check status',
      });
    } finally {
      setCheckingStatus(false);
    }
  }, [username]);

  useEffect(() => {
    checkJDStatus();
  }, [checkJDStatus]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!jobDescription || jobDescription.trim().length < 50) {
      setError('Please enter a job description (minimum 50 characters).');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const token = getToken();
      if (!token) {
        setError('No authentication token available. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/job-description/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        body: JSON.stringify({
          username: username,
          job_description: jobDescription.trim(),
          company_name: companyName.trim() || null,
          role_title: roleTitle.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail ||
            `Failed to upload job description: ${response.statusText}`
        );
      }

      await response.json();
      setSuccess(true);

      // Refresh status
      await checkJDStatus();

      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess(
            jobDescription.trim(),
            companyName.trim() || undefined,
            roleTitle.trim() || undefined
          );
        }, 1000);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to upload job description. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-3 sm:p-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-6 pb-4 sm:pb-6 border-b-2 border-slate-200 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-t-xl sm:rounded-t-2xl">
          <h2 className="text-lg sm:text-2xl font-semibold m-0">
            🎯 Focus Interview Setup
          </h2>
          <button
            className="bg-white/20 hover:bg-white/30 border-none text-white text-2xl sm:text-3xl w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 leading-none p-0 hover:scale-110"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {/* Status Display */}
          {checkingStatus ? (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin"></div>
                <span>Checking status...</span>
              </div>
            </div>
          ) : jdStatus ? (
            <div
              className={`mb-4 p-3 rounded-lg border ${
                jdStatus.has_job_description
                  ? 'bg-green-50 border-green-200'
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {jdStatus.has_job_description ? (
                  <>
                    <span className="text-green-600 text-lg">✓</span>
                    <div className="flex-1">
                      <p className="text-green-800 font-medium text-sm m-0">
                        Job Description Ready
                      </p>
                      {jdStatus.vector_count !== undefined && (
                        <p className="text-green-600 text-xs m-0 mt-1">
                          {jdStatus.vector_count} sections indexed
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-slate-400 text-lg">○</span>
                    <p className="text-slate-600 text-sm m-0">
                      {jdStatus.message || 'No job description uploaded yet'}
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium text-sm m-0">
                ✓ Job description{' '}
                {jdStatus?.has_job_description ? 'updated' : 'uploaded'}{' '}
                successfully! Ready to start your Focus Interview.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm m-0 whitespace-pre-line">
                {error}
              </p>
            </div>
          )}

          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <h3 className="text-emerald-800 font-semibold text-sm mb-2 mt-0">
              How Focus Interview Works
            </h3>
            <ul className="text-emerald-700 text-xs space-y-1 m-0 pl-4">
              <li>Paste the job description from any company</li>
              <li>The AI will analyze the requirements and skills needed</li>
              <li>
                You'll be asked relevant technical, behavioral, and system
                design questions
              </li>
              <li>
                Questions are tailored specifically to the job you're preparing
                for
              </li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Optional Company and Role Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="company-name"
                  className="block mb-2 text-slate-700 font-semibold text-sm"
                >
                  Company Name (Optional)
                </label>
                <input
                  id="company-name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., Google, Amazon, Meta..."
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:border-emerald-500 bg-white"
                  disabled={loading}
                />
              </div>
              <div>
                <label
                  htmlFor="role-title"
                  className="block mb-2 text-slate-700 font-semibold text-sm"
                >
                  Role Title (Optional)
                </label>
                <input
                  id="role-title"
                  type="text"
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  placeholder="e.g., Senior Software Engineer..."
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:border-emerald-500 bg-white"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Job Description Text Input */}
            <div className="mb-4">
              <label
                htmlFor="job-description"
                className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base"
              >
                Job Description *
              </label>
              <textarea
                id="job-description"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job description here including:
- Job title and responsibilities
- Required skills and qualifications  
- Technical requirements (languages, frameworks, tools)
- Experience level and domain knowledge
- Any other relevant information..."
                className="w-full min-h-[300px] px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:border-emerald-500 bg-white resize-y"
                disabled={loading}
              />
              <p className="mt-2 text-xs text-slate-500">
                Copy and paste the complete job description. The more detail you
                provide, the more targeted your interview questions will be.
              </p>
              {jobDescription && (
                <div
                  className={`mt-2 text-xs ${
                    jobDescription.trim().length >= 50
                      ? 'text-green-600'
                      : 'text-amber-600'
                  }`}
                >
                  {jobDescription.trim().length} characters entered{' '}
                  {jobDescription.trim().length < 50 && '(minimum 50 required)'}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                type="submit"
                disabled={
                  loading ||
                  !jobDescription ||
                  jobDescription.trim().length < 50
                }
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-emerald-600 hover:to-teal-700 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </span>
                ) : jdStatus?.has_job_description ? (
                  'Update & Start Interview'
                ) : (
                  'Start Focus Interview'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-slate-200 text-slate-700 rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:bg-slate-300"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>

          {jdStatus?.has_job_description && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-xs sm:text-sm m-0">
                <strong>Note:</strong> Uploading a new job description will
                replace your existing one. The interview will be based on the
                new job description.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDescriptionUpload;
